import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import {
  PayPalScriptProvider,
  PayPalButtons,
} from "@paypal/react-paypal-js";
import {
  Music4, Heart, Sparkles, Coffee, Loader2, KeyRound,
  UploadCloud, FileAudio, Gauge, RefreshCw, Disc3, Activity, Search,
} from "lucide-react";
import KeyExplorer from "@/components/KeyExplorer";
import ProgressionBuilder from "@/components/ProgressionBuilder";
import Community from "@/components/Community";
import AuthModal from "@/components/AuthModal";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BuilderProvider } from "@/context/BuilderContext";
import { LogOut, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESETS = [
  { amount: 3, label: "A little riff", icon: Music4 },
  { amount: 5, label: "Buy a coffee", icon: Coffee },
  { amount: 10, label: "You rock", icon: Sparkles },
];

/* ---------------- Header ---------------- */
function Header({ onNav }) {
  const { user, openAuth, logout } = useAuth();
  const links = [
    ["explore", "Explore"],
    ["find", "Find Key"],
    ["builder", "Builder"],
    ["community", "Community"],
  ];
  return (
    <header className="wtk-header" data-testid="app-header">
      <div className="wtk-header-inner">
        <div className="wtk-brand" data-testid="brand">
          <KeyRound className="wtk-brand-icon" strokeWidth={1.5} />
          <span>What's the Key?</span>
        </div>
        <nav className="wtk-nav">
          {links.map(([id, label]) => (
            <button key={id} className="wtk-navlink wtk-sectionlink" data-testid={`nav-${id}`} onClick={() => onNav(id)}>
              {label}
            </button>
          ))}
          {user ? (
            <div className="wtk-user" data-testid="user-menu">
              <span className="wtk-user-name"><User size={14} /> {user.name}</span>
              <button className="wtk-navlink" data-testid="logout-btn" onClick={logout}><LogOut size={14} /></button>
            </div>
          ) : (
            user === false && (
              <button className="wtk-navlink wtk-signin" data-testid="signin-btn" onClick={() => openAuth("login")}>
                Sign in
              </button>
            )
          )}
          <button className="wtk-tip-nav" data-testid="tip-nav-btn" onClick={() => onNav("tip-section")}>
            <Heart size={16} strokeWidth={2} /> Tip
          </button>
        </nav>
      </div>
    </header>
  );
}

/* ---------------- Key Detector ---------------- */
function KeyDetector() {
  const [file, setFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("upload");
  const [sTitle, setSTitle] = useState("");
  const [sArtist, setSArtist] = useState("");
  const [sLoading, setSLoading] = useState(false);
  const [sError, setSError] = useState(null);
  const [sResults, setSResults] = useState(null);
  const inputRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("wtk_history") || "[]");
      if (Array.isArray(saved)) setHistory(saved);
    } catch (e) {
      /* ignore corrupt history */
    }
  }, []);

  const saveHistory = (next) => {
    setHistory(next);
    try {
      localStorage.setItem("wtk_history", JSON.stringify(next));
    } catch (e) {
      /* storage full or unavailable — keep in-memory only */
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem("wtk_history");
  };

  const pickFile = (f) => {
    if (!f) return;
    setResult(null);
    setError(null);
    setFile(f);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    pickFile(f);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/detect-key`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(res.data);
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
      const entry = {
        id: Date.now(),
        filename: res.data.filename,
        key: res.data.key,
        camelot: res.data.camelot,
        bpm: res.data.bpm,
        confidence: res.data.confidence,
        relative: res.data.relative,
        at: new Date().toISOString(),
      };
      saveHistory([entry, ...history].slice(0, 12));
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          "Could not analyze this file. Please try another audio file."
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const searchByName = async () => {
    if (!sTitle.trim()) return;
    setSLoading(true);
    setSError(null);
    setSResults(null);
    try {
      const res = await axios.get(`${API}/song-key`, {
        params: { title: sTitle, artist: sArtist },
      });
      const list = res.data.results || [];
      setSResults(list);
      if (list.length === 0) {
        setSError("No match found. Try adding the artist name for a better result.");
      } else {
        const top = list.find((r) => r.key) || list[0];
        if (top.key) {
          saveHistory([{
            id: Date.now(),
            filename: `${top.title}${top.artist ? " — " + top.artist : ""}`,
            key: top.key,
            camelot: top.camelot || "—",
            bpm: top.bpm || 0,
            confidence: null,
            relative: null,
            at: new Date().toISOString(),
          }, ...history].slice(0, 12));
        }
      }
    } catch (e) {
      setSError(e.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setSLoading(false);
    }
  };

  return (
    <>
    <section className="wtk-detector" id="find" data-testid="key-detector">
      <div className="wtk-detector-head">
        <div className="wtk-eyebrow"><Disc3 size={15} /> Find a song's key</div>
        <h2 className="wtk-title wtk-title-sm">
          Name it or <span className="wtk-title-accent">play it.</span>
        </h2>
        <p className="wtk-sub">
          Type a song title to look up its key, or upload the audio and we'll
          detect the key, Camelot code, tempo and relative key in seconds.
        </p>
      </div>

      <div className="wtk-detector-card" ref={cardRef}>
        <div className="wtk-tabs" data-testid="detector-tabs">
          <button
            className={`wtk-tab ${tab === "search" ? "is-active" : ""}`}
            data-testid="tab-search"
            onClick={() => setTab("search")}
          >
            <Search size={16} /> Search by name
          </button>
          <button
            className={`wtk-tab ${tab === "upload" ? "is-active" : ""}`}
            data-testid="tab-upload"
            onClick={() => setTab("upload")}
          >
            <UploadCloud size={16} /> Upload audio
          </button>
        </div>

        {tab === "search" ? (
          <div className="wtk-search" data-testid="song-search">
            <div className="wtk-search-fields">
              <input
                className="wtk-text-input"
                data-testid="song-title-input"
                type="text"
                value={sTitle}
                onChange={(e) => setSTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchByName()}
                placeholder="Song title (e.g. Bohemian Rhapsody)"
              />
              <input
                className="wtk-text-input"
                data-testid="song-artist-input"
                type="text"
                value={sArtist}
                onChange={(e) => setSArtist(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchByName()}
                placeholder="Artist (optional, improves accuracy)"
              />
            </div>
            <button
              className="wtk-analyze-btn"
              data-testid="song-search-btn"
              disabled={!sTitle.trim() || sLoading}
              onClick={searchByName}
            >
              {sLoading ? (<><Loader2 className="wtk-spin" size={18} /> Searching…</>) : (<><Search size={18} /> Find the Key</>)}
            </button>

            {sError && <p className="wtk-error" data-testid="song-search-error">{sError}</p>}

            {sResults && sResults.length > 0 && (
              <div className="wtk-search-results" data-testid="song-results">
                {sResults.map((r, i) => (
                  <div className="wtk-search-item" data-testid="song-result-item" key={i}>
                    <div className="wtk-search-key">{r.key || "Key n/a"}</div>
                    <div className="wtk-search-name" title={`${r.title} ${r.artist}`}>
                      {r.title}{r.artist ? ` — ${r.artist}` : ""}
                    </div>
                    <div className="wtk-search-meta">
                      {r.camelot && <span className="wtk-history-badge">{r.camelot}</span>}
                      <span>{r.bpm ? `${r.bpm} BPM` : "BPM n/a"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="wtk-attrib">
              Song data by <a href="https://getsongbpm.com" target="_blank" rel="noreferrer">GetSongBPM</a>
            </p>
          </div>
        ) : !result ? (
          <>
            <div
              className={`wtk-dropzone ${dragging ? "is-drag" : ""} ${file ? "has-file" : ""}`}
              data-testid="file-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <input
                ref={inputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.aiff"
                data-testid="file-input"
                style={{ display: "none" }}
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              {file ? (
                <div className="wtk-file-chip" data-testid="selected-file">
                  <FileAudio size={22} />
                  <div className="wtk-file-meta">
                    <span className="wtk-file-name">{file.name}</span>
                    <span className="wtk-file-size">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <UploadCloud size={40} strokeWidth={1.4} className="wtk-drop-icon" />
                  <p className="wtk-drop-title">Drop an audio file here</p>
                  <p className="wtk-drop-hint">or click to browse · mp3, wav, m4a, flac, ogg</p>
                </>
              )}
            </div>

            {error && (
              <p className="wtk-error" data-testid="detect-error">{error}</p>
            )}

            <button
              className="wtk-analyze-btn"
              data-testid="analyze-btn"
              disabled={!file || loading}
              onClick={analyze}
            >
              {loading ? (
                <><Loader2 className="wtk-spin" size={18} /> Analyzing…</>
              ) : (
                <><Activity size={18} /> Detect Key</>
              )}
            </button>
            {loading && (
              <p className="wtk-analyze-note">Listening to the harmony… this can take a few seconds.</p>
            )}
          </>
        ) : (
          <div className="wtk-result" data-testid="detect-result">
            <div className="wtk-result-key-wrap">
              <span className="wtk-result-label">Detected key</span>
              <div className="wtk-result-key" data-testid="result-key">{result.key}</div>
              <span className="wtk-result-file">{result.filename}</span>
            </div>

            {audioUrl && (
              <div className="wtk-audio" data-testid="audio-player">
                <audio controls src={audioUrl} style={{ width: "100%" }} />
              </div>
            )}

            <div className="wtk-result-grid">
              <div className="wtk-result-cell">
                <span className="wtk-cell-label">Camelot</span>
                <span className="wtk-cell-val" data-testid="result-camelot">{result.camelot}</span>
              </div>
              <div className="wtk-result-cell">
                <span className="wtk-cell-label"><Gauge size={13} /> Tempo</span>
                <span className="wtk-cell-val" data-testid="result-bpm">{result.bpm}<small> BPM</small></span>
              </div>
              <div className="wtk-result-cell">
                <span className="wtk-cell-label">Relative</span>
                <span className="wtk-cell-val wtk-cell-sm" data-testid="result-relative">{result.relative}</span>
              </div>
            </div>

            <div className="wtk-conf">
              <div className="wtk-conf-top">
                <span>Confidence</span>
                <span data-testid="result-confidence">{result.confidence}%</span>
              </div>
              <div className="wtk-conf-bar">
                <div className="wtk-conf-fill" style={{ width: `${result.confidence}%` }} />
              </div>
            </div>

            <button className="wtk-analyze-btn wtk-secondary" data-testid="analyze-another-btn" onClick={reset}>
              <RefreshCw size={17} /> Analyze another track
            </button>
          </div>
        )}
      </div>
    </section>

    {history.length > 0 && (
      <section className="wtk-history" data-testid="recent-history">
        <div className="wtk-history-inner">
          <div className="wtk-history-head">
            <h3 className="wtk-wall-heading"><Disc3 size={16} /> Recently analyzed</h3>
            <button className="wtk-history-clear" data-testid="history-clear" onClick={clearHistory}>
              Clear
            </button>
          </div>
          <div className="wtk-history-grid">
            {history.map((h) => (
              <div key={h.id} className="wtk-history-item" data-testid="history-item">
                <div className="wtk-history-key">{h.key}</div>
                <div className="wtk-history-name" title={h.filename}>{h.filename}</div>
                <div className="wtk-history-meta">
                  <span className="wtk-history-badge">{h.camelot}</span>
                  <span>{h.bpm} BPM</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )}
    </>
  );
}

/* ---------------- Supporters wall ---------------- */
function SupportersWall({ tips }) {
  if (!tips.length) {
    return (
      <p className="wtk-empty" data-testid="supporters-empty">
        No tips yet — be the first to drop a note.
      </p>
    );
  }
  return (
    <ul className="wtk-wall" data-testid="supporters-list">
      {tips.map((t) => (
        <li key={t.id} className="wtk-wall-item" data-testid="supporter-item">
          <div className="wtk-wall-top">
            <span className="wtk-wall-name">{t.name || "Anonymous"}</span>
            <span className="wtk-wall-amt">${t.amount.toFixed(2)}</span>
          </div>
          {t.message && <p className="wtk-wall-msg">"{t.message}"</p>}
        </li>
      ))}
    </ul>
  );
}

/* ---------------- Tip Jar ---------------- */
const TipJar = () => {
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState(5);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [tips, setTips] = useState([]);
  const [stats, setStats] = useState({ total: 0, count: 0 });
  const [status, setStatus] = useState(null);
  const [processing, setProcessing] = useState(false);

  const loadTips = useCallback(async () => {
    try {
      const [tipRes, statRes] = await Promise.all([
        axios.get(`${API}/tips`),
        axios.get(`${API}/tips/stats`),
      ]);
      setTips(tipRes.data);
      setStats(statRes.data);
    } catch (e) {
      console.error("failed loading tips", e);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/tips/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
    loadTips();
  }, [loadTips]);

  const validAmount = Number(amount) > 0;

  const createOrder = async () => {
    const res = await axios.post(`${API}/tips/create-order`, {
      amount: Number(amount),
      currency: "USD",
      name: name || null,
      message: message || null,
    });
    return res.data.id;
  };

  const onApprove = async (data) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/tips/capture-order`, {
        order_id: data.orderID,
        name: name || null,
        message: message || null,
        email: email || null,
      });
      if (res.data.status === "COMPLETED") {
        setStatus("success");
        setName("");
        setEmail("");
        setMessage("");
        loadTips();
      } else {
        setStatus("error");
      }
    } catch (e) {
      console.error(e);
      setStatus("error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <section className="wtk-tip" id="tip-section" data-testid="tip-section">
      <div className="wtk-tip-shell">
        <div className="wtk-tip-hero">
          <div className="wtk-eyebrow"><Heart size={15} /> Support the project</div>
          <h2 className="wtk-tip-title">
            Love the app?
            <br />
            <span className="wtk-title-accent">Leave a tip.</span>
          </h2>
          <p className="wtk-sub">
            Every tip keeps the metronome ticking and the servers humming. Give
            whatever feels right — thank you for the good vibrations.
          </p>

          <div className="wtk-stats" data-testid="tip-stats">
            <div className="wtk-stat">
              <span className="wtk-stat-num" data-testid="stat-total">${stats.total.toFixed(2)}</span>
              <span className="wtk-stat-label">tipped so far</span>
            </div>
            <div className="wtk-stat-divider" />
            <div className="wtk-stat">
              <span className="wtk-stat-num" data-testid="stat-count">{stats.count}</span>
              <span className="wtk-stat-label">kind supporters</span>
            </div>
          </div>

          <div className="wtk-wall-wrap">
            <h3 className="wtk-wall-heading"><Heart size={16} strokeWidth={2} /> Recent supporters</h3>
            <SupportersWall tips={tips} />
          </div>
        </div>

        <div className="wtk-card" data-testid="tip-card">
          {status === "success" ? (
            <div className="wtk-thanks" data-testid="tip-success">
              <div className="wtk-thanks-badge"><Heart size={34} strokeWidth={1.5} /></div>
              <h2>You're a legend!</h2>
              <p>Your tip came through. It genuinely makes a difference.</p>
              <button className="wtk-btn-ghost" data-testid="tip-again-btn" onClick={() => setStatus(null)}>
                Send another
              </button>
            </div>
          ) : (
            <>
              <h2 className="wtk-card-title">Choose an amount</h2>

              <div className="wtk-presets" data-testid="preset-group">
                {PRESETS.map((p) => {
                  const Icon = p.icon;
                  const active = Number(amount) === p.amount;
                  return (
                    <button
                      key={p.amount}
                      type="button"
                      className={`wtk-preset ${active ? "is-active" : ""}`}
                      data-testid={`preset-${p.amount}`}
                      onClick={() => setAmount(p.amount)}
                    >
                      <Icon size={18} strokeWidth={1.75} />
                      <span className="wtk-preset-amt">${p.amount}</span>
                      <span className="wtk-preset-label">{p.label}</span>
                    </button>
                  );
                })}
              </div>

              <label className="wtk-field-label" htmlFor="custom-amount">Or tip any amount</label>
              <div className="wtk-amount-input">
                <span>$</span>
                <input
                  id="custom-amount"
                  data-testid="custom-amount-input"
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                />
                <span className="wtk-cur">USD</span>
              </div>

              <input className="wtk-text-input" data-testid="name-input" type="text" maxLength={60}
                value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" />
              <input className="wtk-text-input" data-testid="email-input" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for a thank-you receipt (optional)" />
              <textarea className="wtk-text-input wtk-textarea" data-testid="message-input" maxLength={280}
                value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Leave a note (optional)" />

              {status === "error" && (
                <p className="wtk-error" data-testid="tip-error">Something went wrong with the payment. Please try again.</p>
              )}

              <div className="wtk-pay" data-testid="paypal-area">
                {config === null ? (
                  <div className="wtk-loading"><Loader2 className="wtk-spin" size={18} /> Loading…</div>
                ) : !config.configured ? (
                  <div className="wtk-notice" data-testid="paypal-not-configured">
                    <strong>PayPal not connected yet.</strong>
                    <span>Add your credentials to <code>backend/.env</code> and restart.</span>
                  </div>
                ) : !validAmount ? (
                  <p className="wtk-hint" data-testid="amount-hint">Enter an amount above to continue.</p>
                ) : (
                  <PayPalScriptProvider options={{ clientId: config.client_id, currency: "USD", intent: "capture" }}>
                    <div data-testid="paypal-buttons">
                      <PayPalButtons
                        key={`${amount}`}
                        style={{ layout: "vertical", shape: "pill", color: "gold" }}
                        disabled={processing}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={() => setStatus("error")}
                      />
                    </div>
                  </PayPalScriptProvider>
                )}
              </div>

              <p className="wtk-secure">
                Secured by PayPal · {config?.mode === "live" ? "Live" : "Sandbox"} mode
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

function App() {
  const onNav = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <AuthProvider>
      <BuilderProvider>
        <div className="wtk-root">
          <div className="wtk-grain" aria-hidden="true" />
          <Header onNav={onNav} />
          <main className="wtk-main">
            <KeyExplorer />
            <KeyDetector />
            <ProgressionBuilder />
            <Community />
            <TipJar />
          </main>
          <footer className="wtk-footer" data-testid="site-footer">
            Song key &amp; BPM data by{" "}
            <a href="https://getsongbpm.com" target="_blank" rel="noreferrer" data-testid="getsongbpm-backlink">GetSongBPM.com</a>
          </footer>
          <AuthModal />
        </div>
      </BuilderProvider>
    </AuthProvider>
  );
}

export default App;
